import React from 'react';
import { ASSETS } from '../../assetManifest.js';
import { CONSTANTS } from '../../data.js';
import { isCompetitorUnlocked, getCompetitorDifficulty } from '../../gameLogic/battleLogic.js';
import BattlePreparationScreen from '../BattlePreparationScreen.jsx';

const BattleTab = ({ 
  battleProgress, 
  selectedCompetitor, 
  playerDragons, 
  onSelectCompetitor, 
  onGoToBattlePrep,
  battle,
  onCancelBattlePrep,
  selectedBattleTeam,
  onSelectBattleDragon,
  onStartBattle
}) => {

  if (battle?.phase === 'preparation') {
    return (
      <BattlePreparationScreen
        playerDragons={playerDragons}
        selectedBattleTeam={selectedBattleTeam}
        onSelectBattleDragon={onSelectBattleDragon}
        onStartBattle={onStartBattle}
        onCancelBattlePrep={onCancelBattlePrep}
        selectedCompetitor={selectedCompetitor}
      />
    );
  }

  const canPrepare = playerDragons.length >= 6;

  return (
    <div className="card-enhanced h-full flex flex-col">
      <div className="flex items-center justify-between mb-4 flex-shrink-0">
        <div className="flex items-center space-x-3">
          <h2 className="text-2xl font-mono font-semibold text-red-400 text-glow">Battle Arena</h2>
        </div>
        {selectedCompetitor && (
          <div title={!canPrepare ? "You need at least 6 dragons to enter this battle." : ""}>
            <button
              onClick={onGoToBattlePrep}
              disabled={!canPrepare}
              className="btn btn-danger px-6 py-2"
            >
              Prepare for Battle
            </button>
          </div>
        )}
      </div>

      <div className="flex-1 overflow-y-auto custom-scrollbar pr-2 -mr-2">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {CONSTANTS.COMPETITORS.map(competitor => {
            const difficulty = getCompetitorDifficulty(competitor.id);
            const isSelected = selectedCompetitor?.id === competitor.id;
            const isUnlocked = isCompetitorUnlocked(competitor.id, battleProgress);

            return (
              <div
                key={competitor.id}
                className={`bg-gray-900/50 p-4 rounded-lg transition-all duration-300 border border-transparent relative overflow-hidden ${
                  !isUnlocked 
                    ? 'opacity-50 cursor-not-allowed' 
                    : isSelected 
                      ? 'border-white/30 scale-105 shadow-lg cursor-pointer' 
                      : 'hover:border-white/20 hover:bg-gray-900/80 cursor-pointer'
                }`}
                style={{ '--difficulty-color': difficulty.color }}
                onClick={() => isUnlocked && onSelectCompetitor(competitor)}
                title={!isUnlocked ? "Win the previous battle to unlock." : ""}
              >
                <div className="absolute top-0 left-0 w-full h-1 bg-[var(--difficulty-color)] opacity-50"></div>
                <div className="flex flex-col items-center mb-3">
                  <div className="relative mb-3">
                    <img
                      src={ASSETS.competitors[competitor.avatar].path}
                      alt={competitor.avatar}
                      className={`w-20 h-20 rounded-full border-2 ${!isUnlocked ? 'filter grayscale' : ''}`}
                      style={{ borderColor: difficulty.color }}
                    />
                    <div 
                      className="absolute -top-1 -right-1 w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold text-black"
                      style={{ backgroundColor: difficulty.color }}
                    >
                      {competitor.id}
                    </div>
                    {!isUnlocked && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full">
                        <div className="text-3xl">🔒</div>
                      </div>
                    )}
                  </div>
                  <div className="text-center">
                    <p className="text-md font-mono font-bold mb-1">{competitor.name}</p>
                    <p className="text-xs font-sans mb-2" style={{ color: difficulty.color }}>
                      {difficulty.text}
                    </p>
                  </div>
                </div>

                <div className="bg-black/20 p-2 rounded">
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-400">Progress:</span>
                    <span className="text-xs" style={{ color: difficulty.color }}>
                      {battleProgress[competitor.id - 1] || 0}/{CONSTANTS.BATTLE_WINS_REQUIRED} wins
                    </span>
                  </div>
                  <div className="w-full bg-black/30 rounded-full h-2.5 mt-1">
                    <div
                      className="h-2.5 rounded-full transition-all duration-300"
                      style={{ 
                        width: `${((battleProgress[competitor.id - 1] || 0) / CONSTANTS.BATTLE_WINS_REQUIRED) * 100}%`,
                        backgroundColor: difficulty.color
                      }}
                    ></div>
                  </div>
                </div>

                {isSelected && isUnlocked && (
                  <div className="absolute top-3 right-3">
                    <div className="w-5 h-5 bg-red-500 rounded-full flex items-center justify-center ring-2 ring-black">
                      <div className="w-2 h-2 bg-white rounded-full"></div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="mt-4 p-3 bg-black/20 rounded-lg flex-shrink-0 border-t border-white/10">
        <p className="text-sm font-mono text-gray-300 mb-2">Battle Requirements:</p>
        <div className="flex items-center space-x-4 text-xs font-sans">
          <div className={`flex items-center space-x-1 ${playerDragons.length >= 6 ? 'text-green-400' : 'text-red-400'}`}>
            <span>🐉</span>
            <span>{playerDragons.length}/6 Dragons</span>
          </div>
          <div className="text-gray-400">
            <span>🏆</span>
            <span>Win {CONSTANTS.BATTLE_WINS_REQUIRED} battles to unlock next competitor</span>
          </div>
          <div className="text-gray-400">
            <span>🗝️</span>
            <span>Earn {CONSTANTS.BATTLE_REWARD_KEYS} keys per victory</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BattleTab;
